import { Layout } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import HeaderBar from 'Layout/HeaderBar';
import FooterBar from 'Layout/FooterBar';
import { functions } from '../../firebase';
import MonitoringRulesPageContent from './MonitoringRulesPageContent';
import { normalizeMonitoringRulesResponse } from './helpers';
import { MonitoringRuleProfileInput, MonitoringRulesResponse } from './types';
import AlertNavigation from './AlertNavigation';

const { Content } = Layout;

async function fetchMonitoringRules(): Promise<MonitoringRulesResponse> {
  const settings = functions.httpsCallable('settings');
  const response: any = await settings({
    requestType: 'GET_MONITORING_RULES',
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode && errorCode !== 200) {
    throw new Error(`Settings API error: ${errorCode}`);
  }

  return normalizeMonitoringRulesResponse(response.data);
}

async function saveMonitoringRuleProfile(input: MonitoringRuleProfileInput) {
  const settings = functions.httpsCallable('settings');
  const response: any = await settings({
    requestType: 'UPSERT_MONITORING_RULE_PROFILE',
    ...input,
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode && errorCode !== 200) {
    throw new Error(`Settings API error: ${errorCode}`);
  }

  return response.data;
}

function MonitoringRulesContainer() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['monitoring-rules'],
    queryFn: fetchMonitoringRules,
    refetchInterval: 60_000,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: saveMonitoringRuleProfile,
    onSuccess: async () => {
      toast.success('Regelprofil gemt');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['monitoring-rules'] }),
        queryClient.invalidateQueries({ queryKey: ['monitoring-overview'] }),
      ]);
    },
    onError: (mutationError: unknown) => {
      toast.error(`Kunne ikke gemme regler: ${(mutationError as Error)?.message}`);
    },
  });

  return (
    <Layout>
      <HeaderBar />
      <Content
        style={{
          margin: '24px 24px 0',
          padding: '0 24px',
        }}
      >
        <AlertNavigation active="legacy" />
        <p className="mb-4 text-sm text-muted-foreground">
          Regler for den ældre overvågning. Enheder med aktiveret samlet alarmstyring
          konfigureres under Enheder og dækning eller Fælles regler.
        </p>
        {isLoading && (
          <div className="py-12 text-center text-sm text-muted-foreground">
            Indlæser monitoreringsregler...
          </div>
        )}
        {isError && (
          <div className="py-12 text-center text-sm text-red-600">
            Kunne ikke hente monitoreringsregler: {(error as Error)?.message}
          </div>
        )}
        {data && (
          <MonitoringRulesPageContent
            response={data}
            isSavingSensorType={mutation.isPending ? mutation.variables?.sensorType || null : null}
            onSave={mutation.mutateAsync}
          />
        )}
      </Content>
      <FooterBar />
    </Layout>
  );
}

export default MonitoringRulesContainer;
